import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import {
  useNotifications,
  NOTIFICATION_LABELS,
  type UnifiedNotification,
  type NotificationStatus,
} from '../../hooks/useNotifications';

/** Bell icon button with unread badge and dropdown notification panel. */
const NotificationBell: React.FC<{ className?: string }> = ({ className = '' }) => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDialogElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { unread, unreadCount, markRead, markAllRead } = useNotifications();

  // Close panel on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleNotificationClick = async (n: UnifiedNotification) => {
    if (n.notificationType === 'nudge') {
      await markRead(n.eventId, undefined, n.id);
      setOpen(false);
      // M-377: activity_update notifications deep-link to the event room;
      // stuck_event_nudge notifications go to the venues tab to prompt voting.
      if (n.type === 'activity_update') {
        navigate(`/room/${n.eventId}`);
      } else {
        navigate(`/room/${n.eventId}?tab=venues`);
      }
    } else {
      // Original event notification logic
      await markRead(n.eventId, n.status as NotificationStatus);
      setOpen(false);
      navigate(`/room/${n.eventId}`);
    }
  };

  const handleMarkAllRead = async () => {
    await markAllRead();
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        aria-label={
          unreadCount > 0 ? `Notifications — ${unreadCount} unread` : 'Notifications — none unread'
        }
        aria-haspopup='true'
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className='relative flex items-center justify-center w-9 h-9 rounded-[10px] transition-colors hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400'
      >
        <Bell
          className='w-[18px] h-[18px]'
          style={{ color: open ? 'var(--color-primary)' : 'var(--color-text-muted)' }}
          strokeWidth={2}
        />
        {unreadCount > 0 && (
          <span
            aria-hidden='true'
            className='absolute top-1 right-1 flex items-center justify-center min-w-[14px] h-[14px] rounded-full text-[9px] font-bold text-white px-[3px]'
            style={{ background: 'linear-gradient(135deg,#2dd4bf,#34d399)' }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <dialog
          ref={panelRef}
          open
          aria-label='Notifications'
          className='absolute right-0 mt-2 w-[320px] rounded-[16px] border border-white/[0.08] shadow-2xl z-50 overflow-hidden p-0'
          style={{ background: '#14142a', top: '100%' }}
        >
          {/* Header */}
          <div className='flex items-center justify-between px-4 py-3 border-b border-white/[0.06]'>
            <p className='text-[13px] font-bold text-white'>Notifications</p>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className='text-[11px] font-semibold text-teal-400 hover:text-teal-300 transition-colors'
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className='max-h-[320px] overflow-y-auto'>
            {unread.length === 0 ? (
              <div className='flex flex-col items-center py-8 px-4 text-center'>
                <Bell className='w-8 h-8 text-dim mb-2' strokeWidth={1.5} />
                <p className='text-[13px] font-semibold text-white mb-0.5'>All caught up</p>
                <p className='text-[11px] text-dim'>No new notifications</p>
              </div>
            ) : (
              unread.map((n) => (
                <button
                  key={n.notificationType === 'nudge' ? n.id : n.eventId}
                  onClick={() => handleNotificationClick(n)}
                  className='w-full text-left flex items-start gap-3 px-4 py-3 border-b border-white/[0.04] hover:bg-white/[0.03] transition-colors last:border-0 focus-visible:outline-none focus-visible:ring-inset focus-visible:ring-1 focus-visible:ring-teal-400'
                >
                  {/* Status indicator dot */}
                  <span className='mt-1.5 w-2 h-2 rounded-full shrink-0 bg-teal-400' />
                  <div className='flex-1 min-w-0'>
                    <p className='text-[12px] font-bold text-white truncate mb-0.5'>
                      {n.eventName}
                    </p>
                    <p className='text-[11px] text-dim leading-relaxed'>
                      {n.notificationType === 'nudge' ? n.message : NOTIFICATION_LABELS[n.status]}
                    </p>
                  </div>
                  <span className='text-[11px] font-semibold text-teal-400 shrink-0 mt-0.5'>
                    Open →
                  </span>
                </button>
              ))
            )}
          </div>
        </dialog>
      )}
    </div>
  );
};

export default NotificationBell;
